import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { UserPlus, Users, BarChart3, Inbox, AlertTriangle, Settings2, ArrowLeftRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

type TabKey = 'unassigned' | 'active' | 'closed';

export function LeadAssignment() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TabKey>('unassigned');
  const [reassignFor, setReassignFor] = useState<any | null>(null);
  const [reassignTo, setReassignTo] = useState<string>('');
  const [reassignReason, setReassignReason] = useState('');

  const { data: agency } = useQuery({
    queryKey: ['my-agency-leadassign', user?.id],
    queryFn: async () => {
      const { data } = await supabase.from('agencies').select('id, name').eq('created_by', user!.id).maybeSingle();
      return data;
    },
    enabled: !!user,
  });

  const { data: settings } = useQuery({
    queryKey: ['agency-settings', agency?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('agency_settings')
        .select('lead_assignment_mode, sla_response_minutes')
        .eq('agency_id', agency!.id)
        .maybeSingle();
      return data || { lead_assignment_mode: 'listing_owner', sla_response_minutes: 30 };
    },
    enabled: !!agency,
  });

  const { data: agents } = useQuery({
    queryKey: ['agency-agents-leadassign', agency?.id],
    queryFn: async () => {
      const { data: ap } = await supabase
        .from('agent_profiles')
        .select('user_id')
        .eq('agency_id', agency!.id);
      const ids = (ap || []).map((a: any) => a.user_id);
      if (!ids.length) return [];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, full_name')
        .in('user_id', ids);
      const map = Object.fromEntries((profiles || []).map((p: any) => [p.user_id, p.full_name]));
      return ids.map((id: string) => ({ user_id: id, full_name: map[id] || 'Agent' }));
    },
    enabled: !!agency,
  });

  // All agency leads (RLS now allows agency admins to see them all)
  const { data: leads, isLoading } = useQuery({
    queryKey: ['agency-inbox-leads', agency?.id, user?.id],
    queryFn: async () => {
      if (!agents || agents.length === 0) return [];
      const ids = agents.map((a: any) => a.user_id);
      const { data } = await supabase
        .from('leads')
        .select('*, properties(title, city), projects(name, city), plots(title, city)')
        .or(`agent_id.in.(${ids.join(',')}),assigned_to.in.(${ids.join(',')})`)
        .order('created_at', { ascending: false })
        .limit(200);
      return data || [];
    },
    enabled: !!agency && !!agents,
  });

  // Agent workload (active leads in last 24h)
  const workload = useMemo(() => {
    const counts: Record<string, number> = {};
    const since = Date.now() - 24 * 3600 * 1000;
    (leads || []).forEach((l: any) => {
      if (l.assigned_to && new Date(l.created_at).getTime() >= since && ['new', 'contacted'].includes(l.status)) {
        counts[l.assigned_to] = (counts[l.assigned_to] || 0) + 1;
      }
    });
    return counts;
  }, [leads]);

  const slaMinutes = settings?.sla_response_minutes ?? 30;
  const isPastSla = (lead: any) =>
    !lead.claimed_at &&
    Date.now() > new Date(lead.created_at).getTime() + slaMinutes * 60 * 1000;

  const buckets = useMemo(() => {
    const all = leads || [];
    return {
      unassigned: all.filter((l: any) => !l.assigned_to && !['converted', 'closed'].includes(l.status)),
      active: all.filter((l: any) => l.assigned_to && ['new', 'contacted'].includes(l.status)),
      closed: all.filter((l: any) => ['converted', 'closed'].includes(l.status)),
    };
  }, [leads]);

  const slaBreaches = buckets.active.filter(isPastSla).length + buckets.unassigned.filter(isPastSla).length;

  const assignLead = useMutation({
    mutationFn: async ({ leadId, agentId }: { leadId: string; agentId: string }) => {
      const { error } = await supabase.functions.invoke('lead-management?action=reassign', {
        body: { lead_id: leadId, to_agent_id: agentId, reason: 'Initial assignment' },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency-inbox-leads'] });
      toast.success('Lead assigned to agent');
    },
    onError: () => toast.error('Failed to assign lead'),
  });

  const reassignMutate = useMutation({
    mutationFn: async () => {
      if (!reassignFor) return;
      const { error } = await supabase.functions.invoke('lead-management?action=reassign', {
        body: { lead_id: reassignFor.id, to_agent_id: reassignTo || null, reason: reassignReason || null },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency-inbox-leads'] });
      setReassignFor(null);
      setReassignTo('');
      setReassignReason('');
      toast.success('Lead reassigned');
    },
    onError: () => toast.error('Failed to reassign'),
  });

  const updatePriority = useMutation({
    mutationFn: async ({ leadId, priority }: { leadId: string; priority: string }) => {
      const { error } = await supabase.from('leads').update({ priority }).eq('id', leadId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency-inbox-leads'] });
    },
  });

  if (!agency) {
    return (
      <Card className="rounded-2xl">
        <CardContent className="p-8 text-center">
          <Users className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">You must own an agency to manage the lead inbox.</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  const renderLeadRow = (lead: any) => {
    const past = isPastSla(lead);
    const listingTitle =
      (lead.properties && (typeof lead.properties.title === 'object' ? lead.properties.title?.en : lead.properties.title))
      || (lead.plots && (typeof lead.plots.title === 'object' ? lead.plots.title?.en : lead.plots.title))
      || (lead.projects && (typeof lead.projects.name === 'object' ? lead.projects.name?.en : lead.projects.name))
      || '—';
    const assignedAgent = agents?.find((a: any) => a.user_id === lead.assigned_to);
    return (
      <TableRow key={lead.id} className={past ? 'bg-destructive/5' : ''}>
        <TableCell>
          <div className="font-medium text-sm flex items-center gap-2">
            {lead.name}
            {lead.priority === 'hot' && <Badge className="bg-destructive/15 text-destructive border-0 text-[10px]">HOT</Badge>}
            {past && <AlertTriangle className="h-3.5 w-3.5 text-destructive" />}
          </div>
          <div className="text-xs text-muted-foreground">{lead.phone}{lead.email ? ` · ${lead.email}` : ''}</div>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground max-w-[180px] truncate">{listingTitle}</TableCell>
        <TableCell className="text-xs">
          <span className="capitalize">{lead.source || 'website'}</span>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {formatDistanceToNow(new Date(lead.created_at), { addSuffix: true })}
          {past && <div className="text-[10px] text-destructive font-semibold">Past SLA ({slaMinutes}m)</div>}
        </TableCell>
        <TableCell>
          <Select value={lead.priority || 'normal'} onValueChange={(v) => updatePriority.mutate({ leadId: lead.id, priority: v })}>
            <SelectTrigger className="w-[90px] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="hot" className="text-xs">Hot</SelectItem>
              <SelectItem value="normal" className="text-xs">Normal</SelectItem>
              <SelectItem value="low" className="text-xs">Low</SelectItem>
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell>
          {!lead.assigned_to ? (
            agents && agents.length > 0 ? (
              <Select onValueChange={(val) => assignLead.mutate({ leadId: lead.id, agentId: val })}>
                <SelectTrigger className="w-[170px] h-8 text-xs">
                  <SelectValue placeholder="Assign agent" />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((a: any) => (
                    <SelectItem key={a.user_id} value={a.user_id} className="text-xs">
                      {a.full_name} ({workload[a.user_id] || 0})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-xs text-muted-foreground">No agents</span>
            )
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-xs">{assignedAgent?.full_name || 'Agent'}</span>
              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => { setReassignFor(lead); setReassignTo(lead.assigned_to || ''); }}>
                <ArrowLeftRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const Empty = ({ msg }: { msg: string }) => (
    <div className="text-center py-12 text-muted-foreground text-sm">{msg}</div>
  );

  const Table_ = ({ items, emptyMsg }: { items: any[]; emptyMsg: string }) =>
    items.length === 0 ? <Empty msg={emptyMsg} /> : (
      <div className="rounded-2xl border overflow-hidden bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Lead</TableHead>
              <TableHead>Listing</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Received</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>{tab === 'unassigned' ? 'Assign To' : 'Agent'}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>{items.map(renderLeadRow)}</TableBody>
        </Table>
      </div>
    );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Inbox className="h-5 w-5 text-primary" /> Agency Lead Inbox
          </h2>
          <p className="text-sm text-muted-foreground">
            Mode: <span className="font-medium text-foreground capitalize">{(settings?.lead_assignment_mode || 'listing_owner').replace('_', ' ')}</span>
            {' · '}SLA: <span className="font-medium text-foreground">{slaMinutes}m</span>
          </p>
        </div>
        <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate('/dashboard/agency-settings')}>
          <Settings2 className="h-4 w-4" /> Routing Settings
        </Button>
      </div>

      {/* Agent Workload Summary */}
      {agents && agents.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-1">
            <BarChart3 className="h-4 w-4" /> Agent Workload (active 24h)
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {agents.map((agent: any) => (
              <Card key={agent.user_id} className="rounded-xl border-0 shadow-sm">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                    {(agent.full_name || 'A').charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{agent.full_name}</div>
                    <div className="text-xs text-muted-foreground">{workload[agent.user_id] || 0} active</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {slaBreaches > 0 && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-destructive/10 text-destructive text-sm">
          <AlertTriangle className="h-4 w-4" />
          <span><strong>{slaBreaches}</strong> lead{slaBreaches > 1 ? 's are' : ' is'} past your SLA window. Reassign or contact now.</span>
        </div>
      )}

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="unassigned" className="gap-2">
            Unassigned <Badge variant="secondary" className="ml-1 text-[10px]">{buckets.unassigned.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="active" className="gap-2">
            Active <Badge variant="secondary" className="ml-1 text-[10px]">{buckets.active.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="closed" className="gap-2">
            Closed <Badge variant="secondary" className="ml-1 text-[10px]">{buckets.closed.length}</Badge>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="unassigned" className="mt-4">
          <Table_ items={buckets.unassigned} emptyMsg="No unassigned leads. 🎉" />
        </TabsContent>
        <TabsContent value="active" className="mt-4">
          <Table_ items={buckets.active} emptyMsg="No active leads in pipeline." />
        </TabsContent>
        <TabsContent value="closed" className="mt-4">
          <Table_ items={buckets.closed} emptyMsg="No closed leads yet." />
        </TabsContent>
      </Tabs>

      <Dialog open={!!reassignFor} onOpenChange={(o) => !o && setReassignFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign Lead</DialogTitle>
            <DialogDescription>Move <strong>{reassignFor?.name}</strong> to another agent. The action is recorded.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">Assign to</label>
              <Select value={reassignTo} onValueChange={setReassignTo}>
                <SelectTrigger><SelectValue placeholder="Select agent" /></SelectTrigger>
                <SelectContent>
                  {(agents || []).map((a: any) => (
                    <SelectItem key={a.user_id} value={a.user_id}>{a.full_name} ({workload[a.user_id] || 0})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium">Reason (optional)</label>
              <Textarea value={reassignReason} onChange={(e) => setReassignReason(e.target.value)} placeholder="e.g. agent is on leave" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignFor(null)}>Cancel</Button>
            <Button onClick={() => reassignMutate.mutate()} disabled={!reassignTo || reassignMutate.isPending}>Reassign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
